// projectWizard/steps/ProjectLearning.jsx — Step 2

import React from 'react';
import Box from '@mui/material/Box';
import { SectionCard, TextInput, ChipsInput, SelectInput, NumberInput, AdvancedPanel } from '../WizardControls';
import { ROLE_OPTIONS } from '../../ProjectFormSections';

const DIFFICULTY_OPTIONS = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
const DURATION_UNITS = ['Days', 'Weeks', 'Months'];

const ProjectLearning = ({ values, errors = {}, setField }) => (
  <SectionCard
    title="Learning"
    description="What a student walks away knowing, and what they need before they start."
  >
    <TextInput
      id="pf-objectives"
      label="Objectives"
      value={values.learning_objectives}
      onChange={(v) => setField('learning_objectives', v)}
      error={errors.learning_objectives}
      hint="The specific skills this project is designed to build."
      placeholder="One objective per line."
      multiline
      minRows={4}
    />

    <ChipsInput
      id="pf-prerequisites"
      label="Prerequisites"
      value={values.prerequisites}
      onChange={(v) => setField('prerequisites', v)}
      error={errors.prerequisites}
      hint="What a student must already know. Type and press Enter."
      placeholder="JavaScript fundamentals, Git basics"
    />

    <TextInput
      id="pf-outcomes"
      label="Expected outcomes"
      value={values.expected_outcomes}
      onChange={(v) => setField('expected_outcomes', v)}
      error={errors.expected_outcomes}
      hint="What the team will have produced and be able to demonstrate."
      placeholder="A deployed application with test coverage and documentation."
      multiline
      minRows={3}
    />

    <ChipsInput
      id="pf-roles"
      label="Roles"
      value={values.preferred_roles}
      onChange={(v) => setField('preferred_roles', v)}
      options={ROLE_OPTIONS}
      error={errors.preferred_roles}
      hint="The roles teams can be formed around. Pick from the list or add your own."
      placeholder="Select roles"
    />

    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
      <SelectInput
        id="pf-difficulty"
        label="Difficulty"
        value={values.difficulty_level}
        onChange={(v) => setField('difficulty_level', v)}
        options={DIFFICULTY_OPTIONS}
        error={errors.difficulty_level}
        hint="Sets expectations before a student enrolls."
      />
      <NumberInput
        id="pf-duration"
        label="Duration"
        value={values.estimated_duration}
        onChange={(v) => setField('estimated_duration', v)}
        error={errors.estimated_duration}
        min={0}
        suffix={values.duration_unit || 'Days'}
        hint="Change the unit under Advanced options."
      />
    </Box>

    <AdvancedPanel count={3}>
      <ChipsInput
        id="pf-recommended-skills"
        label="Recommended skills"
        value={values.recommended_skills}
        onChange={(v) => setField('recommended_skills', v)}
        hint="Helpful but not required."
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
        <SelectInput
          id="pf-duration-unit"
          label="Duration unit"
          value={values.duration_unit}
          onChange={(v) => setField('duration_unit', v)}
          options={DURATION_UNITS}
        />
        <NumberInput
          id="pf-sprints"
          label="Total sprints"
          value={values.total_sprints}
          onChange={(v) => setField('total_sprints', v)}
          min={0}
        />
      </Box>
    </AdvancedPanel>
  </SectionCard>
);

export default React.memo(ProjectLearning);
